// Onboarding API client functions

import type {
  OnboardingSessionResponse,
  OnboardingByCustomerResponse,
  TemplateRecommendation,
} from '../types/onboarding';

const API_URL =
  import.meta.env.VITE_API_URL ||
  'https://niftee-orchestrator.jason-a6e.workers.dev';

/**
 * Get onboarding session by customer ID (used after Stripe redirect)
 */
export async function getOnboardingByCustomer(
  customerId: string
): Promise<OnboardingByCustomerResponse> {
  const response = await fetch(
    `${API_URL}/api/onboarding/by-customer/${customerId}`
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to get onboarding session');
  }

  return response.json();
}

/**
 * Get full onboarding session data
 */
export async function getOnboardingSession(
  sessionId: string
): Promise<OnboardingSessionResponse> {
  const response = await fetch(`${API_URL}/api/onboarding/${sessionId}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to get onboarding session');
  }

  return response.json();
}

/**
 * Update step data (auto-save)
 * Note: step can be a number (1-9) or 'team' for team data
 * @param uiStep - The current UI step number (1-12) for tracking progress
 */
export async function updateOnboardingStep(
  sessionId: string,
  step: number | 'team',
  data: object,
  uiStep?: number
): Promise<{ success: boolean; updatedAt: string }> {
  const response = await fetch(`${API_URL}/api/onboarding/${sessionId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ step, data, uiStep }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to update step');
  }

  return response.json();
}

/**
 * Get template recommendation based on step 1 data
 */
export async function getTemplateRecommendation(
  sessionId: string
): Promise<TemplateRecommendation> {
  const response = await fetch(
    `${API_URL}/api/onboarding/${sessionId}/template-recommendation`
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to get template recommendation');
  }

  return response.json();
}

/**
 * Complete onboarding and trigger site generation
 */
export async function completeOnboarding(
  sessionId: string
): Promise<{ success: boolean; websiteId: string; queueId: string }> {
  const response = await fetch(
    `${API_URL}/api/onboarding/${sessionId}/complete`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to complete onboarding');
  }

  return response.json();
}

/**
 * Upload logo to R2 storage
 */
export async function uploadLogo(
  sessionId: string,
  file: File
): Promise<{ success: boolean; logoUrl: string; filename: string; size: number }> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(
    `${API_URL}/api/onboarding/${sessionId}/upload-logo`,
    {
      method: 'POST',
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to upload logo');
  }

  return response.json();
}

// ============================================
// Domain Registration Mode Check
// ============================================

export interface DomainRegistrationMode {
  mode: 'live' | 'test';
  message: string;
}

/**
 * Check if domain registration is in live or test mode.
 * Used to show a test mode banner in the onboarding UI.
 */
export async function getDomainRegistrationMode(): Promise<DomainRegistrationMode> {
  try {
    const response = await fetch(`${API_URL}/api/domains/registration-mode`);
    if (!response.ok) {
      return { mode: 'test', message: 'Unable to verify registration mode — defaulting to test.' };
    }
    return response.json();
  } catch {
    return { mode: 'test', message: 'Unable to verify registration mode — defaulting to test.' };
  }
}

// ============================================
// Domain Search & Purchase (used during onboarding)
// ============================================

export interface DomainSearchResult {
  domain: string;
  available: boolean;
  premium: boolean;
  price?: number;       // Customer price in cents
  renewalPrice?: number; // Annual renewal in cents
  currency?: string;
}

interface DomainSearchResponse {
  success: boolean;
  query: string;
  results: DomainSearchResult[];
}

interface DomainPurchaseResponse {
  success: boolean;
  message: string;
  purchaseId: string;
  domain: string;
  priceCents: number;
  status: string;
}

/**
 * Search for available domains (public endpoint, no auth needed)
 */
export async function searchDomains(
  query: string
): Promise<DomainSearchResult[]> {
  const response = await fetch(
    `${API_URL}/api/domains/search?query=${encodeURIComponent(query)}`
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(error.error || 'Failed to search domains');
  }

  const data = await response.json() as DomainSearchResponse;
  return data.results;
}

/**
 * Purchase a domain during onboarding.
 * Uses the onboarding session ID to identify the customer.
 */
export async function purchaseDomain(
  sessionId: string,
  domain: string
): Promise<DomainPurchaseResponse> {
  const response = await fetch(
    `${API_URL}/api/onboarding/${sessionId}/purchase-domain`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ domain }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(error.error || 'Failed to purchase domain');
  }

  return response.json();
}

/**
 * Upload gallery image (hero or gallery slot) to R2 storage
 */
export async function uploadGalleryImage(
  sessionId: string,
  file: File,
  slot: string
): Promise<{ success: boolean; imageUrl: string; filename: string; size: number }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('slot', slot);

  const response = await fetch(
    `${API_URL}/api/onboarding/${sessionId}/upload-gallery`,
    {
      method: 'POST',
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to upload image');
  }

  return response.json();
}

// ============================================
// AI Logo Generation
// ============================================

export type LogoStyle = 'classic' | 'modern' | 'bold';

export interface LogoGenerationMode {
  mode: 'live' | 'test';
  message: string;
}

export interface LogoGenerationResult {
  success: boolean;
  generationId: string;
  logoUrl: string;
  roundNumber: number;
  remainingRounds: number;
}

export interface LogoGeneration {
  id: string;
  roundNumber: number;
  style: string;
  logoUrl: string;
  isSelected: boolean;
}

export interface LogoGenerationsResponse {
  success: boolean;
  generations: LogoGeneration[];
  order?: {
    generationsUsed: number;
    generationsAllowed: number;
    isPaid: boolean;
  };
}

/**
 * Check if logo generation is in live or test mode.
 * Used to show a test mode banner in the UI.
 */
export async function getLogoGenerationMode(): Promise<LogoGenerationMode> {
  try {
    const response = await fetch(`${API_URL}/api/logo/mode`);
    if (!response.ok) {
      return { mode: 'test', message: 'Unable to verify generation mode — defaulting to test.' };
    }
    return response.json();
  } catch {
    return { mode: 'test', message: 'Unable to verify generation mode — defaulting to test.' };
  }
}

/**
 * Generate a new AI logo icon.
 * First call creates an order and charges the customer.
 */
export async function generateLogo(
  sessionId: string,
  style: LogoStyle,
  userHint?: string
): Promise<LogoGenerationResult> {
  const response = await fetch(`${API_URL}/api/logo/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      onboardingSessionId: sessionId,
      style,
      userHint: userHint?.trim() || undefined,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(error.error || 'Failed to generate logo');
  }

  return response.json();
}

/**
 * Select a generated logo as the final choice.
 */
export async function selectLogo(
  sessionId: string,
  generationId: string,
  satisfactionRating?: number
): Promise<{ success: boolean; logoUrl: string }> {
  const response = await fetch(`${API_URL}/api/logo/select`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      onboardingSessionId: sessionId,
      generationId,
      satisfactionRating,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(error.error || 'Failed to select logo');
  }

  return response.json();
}

/**
 * Get all logo generations for a session.
 */
export async function getLogoGenerations(
  sessionId: string
): Promise<LogoGenerationsResponse> {
  const response = await fetch(`${API_URL}/api/logo/generations/${sessionId}`);

  if (!response.ok) {
    // Not an error if no generations yet
    return { success: true, generations: [] };
  }

  return response.json();
}
